import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  FolderTree,
  Plus,
  Search,
  Pencil,
  Trash2,
  ListTree,
  List,
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useEntityTypesQuery,
  useCreateEntityTypeMutation,
  useUpdateEntityTypeMutation,
  useDeleteEntityTypeMutation,
  AdminEntityType,
} from '@/client/api/listing-management/entity-types.query';

// `active` is often null/undefined in the payload for live types — treat only
// an explicit 0/false as inactive.
const isActive = (v: unknown) => v !== 0 && v !== false && v !== '0';

// ─── Create / edit dialog ────────────────────────────────────────────────────

const EntityTypeDialog = ({
  open,
  target,
  onClose,
}: {
  open: boolean;
  target: AdminEntityType | null;
  onClose: () => void;
}) => {
  const isEdit = !!target;
  const [form, setForm] = useState({
    name_friendly: target?.name_friendly ?? '',
    name_system: target?.name_system ?? '',
    name_menu: target?.name_menu ?? '',
    etid_parent: target?.etid_parent != null ? String(target.etid_parent) : '',
    description: target?.description ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateEntityTypeMutation();
  const updateMutation = useUpdateEntityTypeMutation();
  const pending = createMutation.isPending || updateMutation.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name_friendly.trim() && !form.name_system.trim()) {
      setError('Provide a friendly name or a system name.');
      return;
    }
    const fields: Record<string, unknown> = {
      name_friendly: form.name_friendly.trim() || undefined,
      name_system: form.name_system.trim() || undefined,
      name_menu: form.name_menu.trim() || undefined,
      description: form.description.trim() || undefined,
      etid_parent: form.etid_parent ? Number(form.etid_parent) : undefined,
    };
    try {
      if (isEdit && target?.etid) {
        await updateMutation.mutateAsync({ etid: target.etid, fields });
        toast.success('Category updated');
      } else {
        await createMutation.mutateAsync(fields);
        toast.success('Category created');
      }
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save the category.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5 text-indigo-600" />
            {isEdit ? 'Edit category' : 'New category'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="et-friendly">Friendly name</Label>
              <Input
                id="et-friendly"
                placeholder="Apartments"
                value={form.name_friendly}
                onChange={(e) => setForm((p) => ({ ...p, name_friendly: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="et-system">System name</Label>
              <Input
                id="et-system"
                placeholder="apartments"
                value={form.name_system}
                onChange={(e) => setForm((p) => ({ ...p, name_system: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="et-parent">Parent etid</Label>
              <Input
                id="et-parent"
                inputMode="numeric"
                placeholder="optional"
                value={form.etid_parent}
                onChange={(e) => setForm((p) => ({ ...p, etid_parent: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="et-menu">Menu name</Label>
              <Input
                id="et-menu"
                placeholder="optional"
                value={form.name_menu}
                onChange={(e) => setForm((p) => ({ ...p, name_menu: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="et-desc">Description</Label>
            <Input
              id="et-desc"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Create category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Row ─────────────────────────────────────────────────────────────────────

const TypeRow = ({
  t,
  depth,
  onEdit,
  onDelete,
}: {
  t: AdminEntityType;
  depth: number;
  onEdit: (t: AdminEntityType) => void;
  onDelete: (t: AdminEntityType) => void;
}) => (
  <tr className="hover:bg-gray-50">
    <td className="px-6 py-3">
      <div className="flex items-center gap-2" style={{ paddingLeft: depth * 20 }}>
        {depth > 0 && <span className="text-gray-300">└</span>}
        <span className="text-sm font-medium text-gray-900">
          {t.name_friendly || t.name_system || `Type ${t.etid}`}
        </span>
      </div>
    </td>
    <td className="whitespace-nowrap px-6 py-3">
      <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
        {t.name_system ?? '—'}
      </code>
    </td>
    <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">{t.etid ?? '—'}</td>
    <td className="whitespace-nowrap px-6 py-3">
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          isActive(t.active) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {isActive(t.active) ? 'Active' : 'Inactive'}
      </span>
    </td>
    <td className="whitespace-nowrap px-6 py-3 text-right">
      <div className="flex items-center justify-end gap-3">
        <button onClick={() => onEdit(t)} className="text-blue-600 hover:text-blue-800" title="Edit">
          <Pencil className="h-4 w-4" />
        </button>
        <button onClick={() => onDelete(t)} className="text-red-600 hover:text-red-800" title="Delete">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </td>
  </tr>
);

// ─── Page ────────────────────────────────────────────────────────────────────

const CategoriesAndTags = () => {
  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Categories & Tags' },
  ]);

  // View mode, search and pagination persist in the URL (?hierarchy=&q=&page=&limit=).
  const {
    filters,
    handleFilterChange,
    searchTerm,
    handleSearch,
    page,
    limit,
    handlePageChange,
    handleLimitChange,
  } = useListFilters<{ hierarchy: string }>({
    initialFilters: { hierarchy: '' },
    initialLimit: 25,
  });
  const hierarchy = filters.hierarchy === 'true';
  const search = searchTerm;
  const perPage = limit;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminEntityType | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminEntityType | null>(null);

  const { data, isLoading, isError } = useEntityTypesQuery(hierarchy);
  const deleteMutation = useDeleteEntityTypeMutation();

  const types = data?.entity_types ?? [];

  const flatRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const match = (t: AdminEntityType) =>
      !term ||
      (t.name_friendly ?? '').toLowerCase().includes(term) ||
      (t.name_system ?? '').toLowerCase().includes(term) ||
      String(t.etid ?? '').includes(term);

    if (!hierarchy) return types.filter(match).map((t) => ({ t, depth: 0 }));

    const out: { t: AdminEntityType; depth: number }[] = [];
    const walk = (nodes: AdminEntityType[], depth: number) => {
      nodes.forEach((n) => {
        out.push({ t: n, depth });
        if (Array.isArray(n.children) && n.children.length) walk(n.children, depth + 1);
      });
    };
    walk(types, 0);
    return term ? out.filter(({ t }) => match(t)) : out;
  }, [types, hierarchy, search]);

  // The endpoint returns the full set, so pagination is client-side.
  const total = flatRows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pagedRows = useMemo(
    () => flatRows.slice((page - 1) * perPage, (page - 1) * perPage + perPage),
    [flatRows, page, perPage]
  );


  const handleDelete = async (hard: boolean) => {
    if (!pendingDelete?.etid) return;
    try {
      await deleteMutation.mutateAsync({ etid: pendingDelete.etid, hard });
      toast.success(hard ? 'Category deleted' : 'Category deactivated');
      setPendingDelete(null);
    } catch (err: any) {
      if (err?.status === 409) {
        toast.error('In use — has child types or listings. Reassign them before hard-deleting.');
      } else {
        toast.error(err?.message ?? 'Failed to delete the category.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderTree className="h-7 w-7 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Categories &amp; Tags</h1>
              <p className="text-sm text-gray-500">
                Manage entity types (categories) — create, edit, and (de)activate.
              </p>
            </div>
          </div>
          <Button
            className="gap-2"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" /> New category
          </Button>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Filter by name or etid…"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => handleFilterChange('hierarchy', hierarchy ? '' : 'true')}>
            {hierarchy ? <List className="h-4 w-4" /> : <ListTree className="h-4 w-4" />}
            {hierarchy ? 'Flat list' : 'Tree view'}
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Category', 'System name', 'etid', 'Status', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                        h === 'Actions' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && isError && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-red-600">
                      Could not load categories.
                    </td>
                  </tr>
                )}
                {!isLoading && !isError && flatRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                      No categories found.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  pagedRows.map(({ t, depth }) => (
                    <TypeRow
                      key={t.etid ?? `${t.name_system}-${depth}`}
                      t={t}
                      depth={depth}
                      onEdit={(x) => {
                        setEditing(x);
                        setDialogOpen(true);
                      }}
                      onDelete={setPendingDelete}
                    />
                  ))}
              </tbody>
            </table>
          </div>

          {!isLoading && !isError && total > 0 && (
            <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>
                  {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
                </span>
                <select
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs"
                  value={perPage}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(Math.max(1, page - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm font-medium text-gray-700">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-3 text-xs text-gray-500">
          Global tags live in the{' '}
          <a href="/admin/content/tags" className="font-medium text-blue-700 hover:underline">
            Tags taxonomy workspace
          </a>
          . Assign them on listing detail (Tags section) or in bulk from a tag&apos;s detail drawer.
        </p>
      </div>

      {dialogOpen && (
        <EntityTypeDialog
          key={editing?.etid ?? 'create'}
          open={dialogOpen}
          target={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete “{pendingDelete?.name_friendly || pendingDelete?.name_system}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Deactivate</strong> keeps the record but hides it.{' '}
              <strong>Hard delete</strong> removes it entirely and is refused (409) if it still has
              child types or listings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => handleDelete(false)} disabled={deleteMutation.isPending}>
              Deactivate
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(true)} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Working…' : 'Hard delete'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export { CategoriesAndTags };
